import React from 'react';
import {
  IntegrationSpec,
  ReleaseStatusDeprecated,
  ReleaseStatusNotAvailable,
} from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { authnClient } from '@frontend/auth';
import { localStorageHelper } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { TableColumnConfig } from '@frontend/design-system';
import { appConfig } from '../envconfig';
import { useIntegrationsDashboardStore } from '../store';
import { FeatureStatus, UserRole, Verticals } from './constants';
import { RoleBasedValue, TableData, ColumnFilterValues, IntegrationFilterDefinition } from './typings';

dayjs.extend(duration);

type FeatureColumnGroup = {
  id: string;
  Header: string;
  columns: TableColumnConfig<TableData>[];
  headerAlign?: 'center';
  sticky?: 'left' | 'right';
};

const CAPITAL_SPLIT_REGEX = /([a-z])([A-Z])/g; //  Regex to find boundaries between a lowercase and an uppercase letter to split strings.
const SEPARATORS_REGEX = /[-_.]+/g; //  Regex to match common separators like hyphens, underscores, or periods.
const MULTIPLE_SPACES_REGEX = /\s+/g; //  Regex to match one or more whitespace characters.

/**
 * Returns the user-facing name for an integration, preferring `rootName` and falling back to
 * `displayName`. Use this everywhere the integration is rendered or slugged so the table,
 * detail page, modals, and route slug stay consistent.
 */
export const getIntegrationName = (integration?: Pick<IntegrationSpec, 'rootName' | 'displayName'>): string =>
  integration?.rootName?.trim() || integration?.displayName?.trim() || '';

/**
 * Returns true when running on localhost or a dev environment.
 */
const isDevEnvironment = (): boolean => {
  return appConfig.VITE_WEAVE_API.includes('api.weavedev.net');
};

/**
 * Cache to avoid recomputing integration operation strings.
 * Key is `${dataType}_${operation}`, value is { formatted, camelCase }.
 */
const integrationOperationCache = new Map<string, { formatted: string; camelCase: string }>();

/**
 * Normalizes a string by splitting camelCase, replacing separators with spaces, and trimming whitespace.
 * @param {string} input The string to normalize.
 * @returns {string} The normalized, space-separated string.
 */
const normalizeString = (input: string): string =>
  (input || '')
    .replace(CAPITAL_SPLIT_REGEX, '$1 $2')
    .replace(SEPARATORS_REGEX, ' ')
    .replace(MULTIPLE_SPACES_REGEX, ' ')
    .trim();

/**
 * Formats a string into Title Case, where each word is capitalized.
 * @param {string} input The string to format.
 * @returns {string} The string in Title Case format.
 */
export const formatString = (input: string): string =>
  normalizeString(input)
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/**
 * Converts a string into camelCase format.
 * @param {string} input The string to convert.
 * @returns {string} The camelCased string.
 */
export const toCamelCase = (input: string): string =>
  normalizeString(input)
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (m, idx) => (idx === 0 ? m.toLowerCase() : m.toUpperCase()))
    .replace(MULTIPLE_SPACES_REGEX, '');

/**
 * Builds an integration operation label with both titleCase and camelCase forms.
 * Uses an internal cache to avoid recomputing labels for the same `dataType` + `operation` combination.
 *
 * Cache key format:
 *  - `${dataType}_${operation}` when operation is provided
 *  - `${dataType}` when operation is undefined
 *
 * @param dataType - The integration data type (e.g., 'user', 'deviceInfo').
 * @param operation - Optional operation name (e.g., 'create', 'read').
 * @returns An object containing:
 *  - formatted: Title-cased (e.g., "User Create")
 *  - camelCase: camelCased identifier (e.g., "userCreate")
 */
export const buildOperationLabel = (dataType: string, operation?: string): { formatted: string; camelCase: string } => {
  const key = operation ? `${dataType}_${operation}` : dataType;
  if (integrationOperationCache.has(key)) {
    return integrationOperationCache.get(key) as {
      formatted: string;
      camelCase: string;
    };
  }

  const combined = operation ? `${dataType} ${operation}` : dataType;
  const result = {
    formatted: formatString(combined),
    camelCase: toCamelCase(combined),
  };

  integrationOperationCache.set(key, result);
  return result;
};

/**
 * @constant typeColorMap
 * @description A map to associate integration connection types with specific colors for UI chips.
 */
const typeColorMap: Record<string, string> = {
  'Integration Type Unspecified': 'critical',
  'On Prem': 'warn',
  Cloud: 'primary',
  'File Upload': 'seaweed',
  'Manual Entry': 'indigo',
};

/**
 * Gets a color string based on the integration connection type.
 * @param {string} type The integration connection type (e.g., 'Cloud', 'On Premise').
 * @returns {string | undefined} The corresponding color string or undefined if not found.
 */
export const getTypeColor = (type: string): string | undefined => typeColorMap[buildOperationLabel(type).formatted];

/**
 * Formats a date into a clean, readable "D MMM YYYY h:mm A" format.
 * @param {string | number | Date} date The date to format.
 * @returns {string} The formatted date string (e.g., "3 May 2026 9:30 AM"), or 'N/A' if invalid.
 */
export const cleanDate = (date: string | number | Date): string => {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('D MMM YYYY h:mm A') : 'N/A';
};

/**
 * Collects the de-duplicated feature names used as dynamic table columns.
 */
export const getUniqueSupportedFeatures = (integrations?: IntegrationSpec[]): string[] => {
  const featureNames = new Set<string>();

  // In development, scan all integrations to aggregate every unique feature,
  // as mock data might have varying features across different integrations.
  // In production, optimize by assuming the first integration contains the
  // definitive master list of all possible supported features.
  if (isDevEnvironment()) {
    for (const integration of integrations ?? []) {
      if (getIntegrationName(integration).toLowerCase() === 'mockpms') {
        continue;
      }
      const supportedFeatures = integration.supportedFeatures ?? [];
      for (const feature of supportedFeatures) {
        const featureName = feature.feature?.trim();
        if (featureName) featureNames.add(featureName);
      }
    }
  } else {
    const firstIntegration = integrations?.[0];
    if (firstIntegration) {
      for (const feature of firstIntegration.supportedFeatures ?? []) {
        const featureName = feature.feature?.trim();
        if (featureName) featureNames.add(featureName);
      }
    }
  }

  return Array.from(featureNames).sort((first, second) =>
    first.localeCompare(second, undefined, { sensitivity: 'base' })
  );
};

const getFeatureGroup = (featureName: string): string => featureName.split('-')[0].trim();

const getFeatureSubheader = (featureName: string): string => {
  const separatorIndex = featureName.indexOf('-');
  return separatorIndex === -1 ? featureName : featureName.slice(separatorIndex + 1).trim() || featureName;
};

const getFeatureHeaderTitle = (featureName: string): string => {
  const subHeader = getFeatureSubheader(featureName);
  return subHeader === featureName ? featureName : `${getFeatureGroup(featureName)} - ${subHeader}`;
};

/**
 * Builds the integrations table's columns, rows, and per-row vertical tags.
 * Vertical filtering is intentionally NOT applied here so the caller can
 * memoize this expensive build on `integrations` alone and apply the cheap
 * vertical filter separately.
 */
const FILTERABLE_STATIC_COLUMN_LABELS: Record<string, string | undefined> = {
  tier: 'Tier',
  type: 'Type',
  partnerStatus: 'Partnership Status',
  releaseStatus: 'Release Status',
};

export const buildIntegrationsTable = (
  integrations?: IntegrationSpec[],
  { includeHiddenReleaseStatuses = false }: { includeHiddenReleaseStatuses?: boolean } = {}
): {
  integrationsTableColumns: TableColumnConfig<TableData>[];
  integrationsTableData: TableData[];
  rowVerticals: string[][];
  rowSearchText: Map<TableData, string>;
  filterDefinitions: IntegrationFilterDefinition[];
  staticColumnIds: string[];
} => {
  // Defines the static columns present in the integrations table.
  const staticColumns: TableColumnConfig<TableData>[] = [
    {
      id: 'integration',
      Header: 'Integration',
      accessor: 'integration',
      minWidth: 150,
      sortType: 'string',
      sticky: 'left',
    },
    {
      id: 'tier',
      Header: 'Tier',
      accessor: 'tier',
      minWidth: 100,
      sortType: 'string',
    },
    {
      id: 'supportedFeaturesCount',
      Header: 'Supported Features Count',
      accessor: 'supportedFeaturesCount',
      minWidth: 180,
      sortType: 'string',
      headerAlign: 'center',
      cellAlign: 'center',
    },
    {
      id: 'type',
      Header: 'Type',
      accessor: 'type',
      minWidth: 150,
      sortType: 'string',
      cellConfig: { element: 'Chip', variant: (type: string) => getTypeColor(type) },
    },
    {
      id: 'partnerStatus',
      Header: 'Partnership Status',
      accessor: 'partnerStatus',
      minWidth: 150,
      sortType: 'string',
      headerAlign: 'center',
      cellAlign: 'center',
    },
    {
      id: 'releaseStatus',
      Header: 'Release Status',
      accessor: 'releaseStatus',
      minWidth: 150,
      sortType: 'string',
      headerAlign: 'center',
      cellAlign: 'center',
    },
    {
      id: 'gaReleaseDate',
      Header: 'GA Date',
      accessor: 'gaReleaseDate',
      width: 150,
      sortType: 'date',
      headerAlign: 'center',
      cellAlign: 'center',
    },
    {
      id: 'lastUpdated',
      Header: 'Last Updated',
      accessor: 'lastUpdated',
      width: 150,
      sortType: 'date',
      headerAlign: 'center',
      cellAlign: 'center',
    },
  ];

  const uniqueSupportedFeatures = getUniqueSupportedFeatures(integrations);

  const featureColumns = uniqueSupportedFeatures.map((feature): TableColumnConfig<TableData> => {
    const headerTitle = getFeatureHeaderTitle(feature);

    return {
      id: toCamelCase(feature),
      Header: React.createElement(
        'span',
        {
          title: headerTitle,
        },
        headerTitle
      ),
      minWidth: 200,
      accessor: toCamelCase(feature),
      headerAlign: 'center',
      cellAlign: 'center',
      cellRenderer: (value) =>
        value === FeatureStatus.SUPPORTED
          ? React.createElement(Icon, { name: 'confirm-badge', color: 'success' })
          : React.createElement(Icon, { name: 'error-badge', color: 'error' }),
    };
  });

  // Groups feature columns by the prefix before the first hyphen.
  const featureColumnsByGroup = new Map<string, TableColumnConfig<TableData>[]>();
  featureColumns.forEach((column, index) => {
    const group = getFeatureGroup(uniqueSupportedFeatures[index]);
    const columns = featureColumnsByGroup.get(group) ?? [];
    columns.push(column);
    featureColumnsByGroup.set(group, columns);
  });
  const dynamicColumns: FeatureColumnGroup[] = [];
  for (const [group, columns] of featureColumnsByGroup) {
    dynamicColumns.push({
      id: `featureGroup${toCamelCase(group)}`,
      Header: group,
      columns,
      headerAlign: 'center',
    });
  }

  // Keep every top-level column at the same depth so react-table preserves
  // static columns before grouped feature headers.
  const staticColumnGroups: FeatureColumnGroup[] = [];
  for (const column of staticColumns) {
    staticColumnGroups.push({
      id: `staticGroup${String(column.id)}`,
      Header: '',
      columns: [column],
      sticky: column.sticky,
    });
  }
  const integrationsTableColumns = [...staticColumnGroups, ...dynamicColumns] as TableColumnConfig<TableData>[];
  const leafColumns = [...staticColumns, ...featureColumns];

  // Maps known column ids to accessor functions to extract data from integrations.
  // `featureCounts` is precomputed per row so `supportedFeaturesCount` is O(1) here.
  const predefinedAccessors: Record<
    string,
    (integration: IntegrationSpec, featureCounts: { supported: number; total: number }) => string
  > = {
    integration: (integration) => getIntegrationName(integration) || 'N/A',
    tier: (integration) =>
      integration?.tier && integration.tier !== 'TBD' ? buildOperationLabel(String(integration.tier)).formatted : 'N/A',
    supportedFeaturesCount: (_integration, { supported, total }) => `${supported}/${total}`,
    type: (integration) => (integration?.type ? buildOperationLabel(integration.type).formatted : 'N/A'),
    partnerStatus: (integration) => integration?.partnerInfo?.partnerStatus?.trim() || 'N/A',
    releaseStatus: (integration) =>
      integration?.releaseStatus
        ? integration.releaseStatus.toLowerCase() === 'ga'
          ? integration.releaseStatus.toUpperCase()
          : formatString(integration.releaseStatus)
        : 'N/A',

    gaReleaseDate: (integration) => (integration?.gaReleaseDate ? cleanDate(integration?.gaReleaseDate) : 'N/A'),
    lastUpdated: (integration) => (integration?.lastUpdated ? cleanDate(integration?.lastUpdated) : 'N/A'),
  };

  /**
   * Normalizes vertical values into a lowercase string array.
   *
   * Accepts either a single vertical or an array of verticals and ensures the result is always an array of lowercase strings.
   */
  const normalizeVerticals = (verticals?: string | string[]) => {
    if (!verticals) return [];
    return Array.isArray(verticals) ? verticals.map((v) => v.toLowerCase()) : [verticals.toLowerCase()];
  };

  const showMockIntegrations = isDevEnvironment();

  const hiddenReleaseStatuses = new Set(
    [ReleaseStatusDeprecated, ReleaseStatusNotAvailable].map((s) => s.toLowerCase())
  );

  // Exclude MockPMS in production; vertical filtering is deferred to the caller.
  const visibleIntegrations =
    integrations?.filter(
      (integration) =>
        (includeHiddenReleaseStatuses || !hiddenReleaseStatuses.has(integration.releaseStatus?.toLowerCase() ?? '')) &&
        (showMockIntegrations || getIntegrationName(integration).toLowerCase() !== 'mockpms')
    ) ?? [];

  // Track longest cell value per column while building rows so width sizing
  // does not require a second O(columns x rows) pass.
  const cellMaxLenByColumn: Record<string, number> = {};

  // Build each row in a single pass per integration: compute feature statuses
  // and counts once, then resolve every column against them.
  const rowVerticals: string[][] = [];
  const rowSearchText = new Map<TableData, string>();
  const integrationsTableData: TableData[] = visibleIntegrations.map((integration) => {
    const features = integration?.supportedFeatures ?? [];
    const featureStatusByColumn = new Map(
      features.flatMap((feature) => {
        const featureName = feature.feature?.trim();
        return featureName ? [[toCamelCase(featureName), feature.featureStatus ?? FeatureStatus.NOT_SUPPORTED]] : [];
      })
    );
    let supportedCount = 0;
    for (const f of features) {
      if (f?.featureStatus === FeatureStatus.SUPPORTED) supportedCount++;
    }
    const featureCounts = { supported: supportedCount, total: features.length };

    const rowData = {} as TableData;
    leafColumns.forEach((column) => {
      const accessor = predefinedAccessors[column.id];
      const value = accessor
        ? accessor(integration, featureCounts)
        : featureStatusByColumn.get(column.id) ?? FeatureStatus.NOT_SUPPORTED;
      rowData[column.id] = value;
      if (typeof value === 'string' && value.length > (cellMaxLenByColumn[column.id] ?? 0)) {
        cellMaxLenByColumn[column.id] = value.length;
      }
    });

    rowVerticals.push(normalizeVerticals(integration.verticals));
    rowSearchText.set(
      rowData,
      [integration.displayName, integration.integrationName, integration.name, integration.rootName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    );
    return rowData;
  });

  // Size each column to its longest cell value, ensuring the header still fits
  // within HEADER_MAX_LINES (wrap handled by header CSS in DashboardView).
  const CHAR_WIDTH = 8;
  const COLUMN_PADDING = 56;
  const MIN_COLUMN_WIDTH = 150;
  const MAX_COLUMN_WIDTH = 600;
  const HEADER_MAX_LINES = 3;
  leafColumns.forEach((column) => {
    const cellMaxLen = cellMaxLenByColumn[column.id] ?? 0;
    const headerText = typeof column.Header === 'string' ? column.Header : '';
    const cellWidth = cellMaxLen * CHAR_WIDTH + COLUMN_PADDING;
    // Reserve at least the longest word so word-break never truncates it.
    const longestWordLen = headerText.split(/\s+/).reduce((max, word) => Math.max(max, word.length), 0);
    const headerCharsPerLine = Math.max(Math.ceil(headerText.length / HEADER_MAX_LINES), longestWordLen);
    const headerWrapWidth = headerCharsPerLine * CHAR_WIDTH + COLUMN_PADDING;
    const width = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.max(cellWidth, headerWrapWidth)));
    (column as { width?: number; minWidth?: number }).width = width;
    (column as { width?: number; minWidth?: number }).minWidth = width;
  });

  // Filter ids must equal leaf column ids so hiding a column can hide its filter.
  const filterableColumns: Array<Omit<IntegrationFilterDefinition, 'options'>> = [];
  for (const column of staticColumns) {
    const id = String(column.id);
    const label = FILTERABLE_STATIC_COLUMN_LABELS[id];
    if (label) filterableColumns.push({ id, label });
  }
  featureColumns.forEach((column, index) => {
    filterableColumns.push({ id: String(column.id), label: getFeatureHeaderTitle(uniqueSupportedFeatures[index]) });
  });
  const filterDefinitions: IntegrationFilterDefinition[] = filterableColumns.map((column) => ({
    ...column,
    options: Array.from(
      new Set(integrationsTableData.map((row) => row[column.id]).filter((value) => typeof value === 'string'))
    ).sort((a, b) => a.localeCompare(b)),
  }));

  const staticColumnIds: string[] = [];
  for (const column of staticColumns) staticColumnIds.push(String(column.id));

  return {
    integrationsTableColumns,
    integrationsTableData,
    rowVerticals,
    rowSearchText,
    filterDefinitions,
    staticColumnIds,
  };
};

/**
 * Keeps only filters whose column exists and whose value is still one of that column's options.
 */
export const getValidColumnFilters = (
  filters: ColumnFilterValues,
  filterDefinitions: IntegrationFilterDefinition[]
): ColumnFilterValues =>
  filterDefinitions.reduce<ColumnFilterValues>((acc, { id, options }) => {
    const value = filters[id];
    if (value && options.includes(value)) acc[id] = value;
    return acc;
  }, {});

/**
 * Applies column filters as exact matches. Expects filters already passed through getValidColumnFilters.
 */
export const filterRowsByColumnFilters = (rows: TableData[], validFilters: ColumnFilterValues): TableData[] => {
  const activeFilters = Object.entries(validFilters);
  if (!activeFilters.length) return rows;
  return rows.filter((row) => activeFilters.every(([id, value]) => String(row[id] ?? '') === value));
};

/**
 * Filters pre-built table rows by the selected vertical. Cheap O(rows) pass
 * suitable for running on every vertical toggle without rebuilding columns.
 */
export const filterRowsByVertical = (
  rows: TableData[],
  rowVerticals: string[][],
  selectedVertical?: string
): TableData[] => {
  const normalized = selectedVertical?.toLowerCase();
  if (!normalized || normalized === Verticals.ALL.toLowerCase()) return rows;
  return rows.filter((_row, i) => rowVerticals[i]?.includes(normalized));
};

export const isVertical = (value: string): value is Verticals => Object.values<string>(Verticals).includes(value);

/**
 * Checks whether the user is authenticated.
 *
 * The function verifies authentication by:
 * - Reading the `isUserAuthenticated` state from the integrations dashboard store and auth helper.
 * - Ensuring both `token` and `user` exist in localStorage.
 *
 * @returns {boolean} `true` if the user is authenticated, otherwise `false`.
 */
export const checkIsUserAuthenticated = (): boolean => {
  const { isUserAuthenticated } = useIntegrationsDashboardStore.getState();

  const token = localStorageHelper.get('token');
  const user = localStorageHelper.get('user');
  return isUserAuthenticated && !!token && !!user && authnClient.isUserAuthenticated();
};

/**
 * Evaluates RBAC (Role-Based Access Control) permissions for a section
 * and determines whether the current user has view and/or edit access.
 *
 * View access is granted if:
 * - `UserRole.ALL` is included in `accessibleByRoles`, or
 * - the user's role exists in `accessibleByRoles`, or
 * - the user's role is listed as an owner.
 *
 * Edit access is granted only if:
 * - the user's role is listed as an owner.
 *
 * @param {RoleBasedValue} [section] - Section configuration containing RBAC rules such as `accessibleByRoles` and `owners`.
 * @param {keyof typeof UserRole} [userRole] - The current user's role.
 *
 * @returns {{ hasViewAccess: boolean; hasEditAccess: boolean }}
 * An object indicating whether the user can view and/or edit the section.
 * If `section` or `userRole` is undefined, both permissions are `false`.
 */

export const checkRbacAccess = (
  section?: RoleBasedValue,
  userRole?: keyof typeof UserRole
): { hasViewAccess: boolean; hasEditAccess: boolean } => {
  if (!section || !userRole) return { hasViewAccess: false, hasEditAccess: false };

  const { accessibleByRoles = [], owners = [] } = section;
  const rolesSet = new Set(accessibleByRoles);
  const ownersSet = new Set(owners);

  const hasViewAccess = rolesSet.has(UserRole.ALL) || rolesSet.has(userRole) || ownersSet.has(userRole);
  const hasEditAccess = ownersSet.has(userRole);

  return { hasViewAccess, hasEditAccess };
};

/**
 * Ensures a URL has an absolute protocol prefix.
 * If the URL already starts with `http://` or `https://`, it is returned as-is.
 * Otherwise, `https://` is prepended to prevent the browser from treating it as a relative path.
 *
 * @param {string} url - The raw URL string (e.g., `"example.com"` or `"https://example.com"`).
 * @returns {string} A URL guaranteed to start with `http://` or `https://`.
 */
export const normalizeUrl = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/**
 * Formats an ISO timestamp into a human-readable display string.
 *
 * @param {string | null | undefined} ts - ISO timestamp to format.
 * @returns {string} Formatted timestamp (e.g., "03 May 2026 · 09:30:15 AM"), or an em-dash if the input is missing or invalid.
 */
export const formatTs = (ts: string | null | undefined) => {
  if (!ts) return '—';
  const parsed = dayjs(ts);
  if (!parsed.isValid()) return '—';
  return parsed.format('DD MMM YYYY · hh:mm:ss A');
};

// Parse an ISO timestamp to epoch microseconds, preserving sub-millisecond precision
// (Date / dayjs only retain milliseconds, which loses information for backend timestamps
// that include microseconds, e.g. "...558144Z").
export const toEpochMicros = (ts: string | null | undefined): number | null => {
  if (!ts) return null;
  const match = ts.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})?$/);
  if (!match) return null;
  const [, datePart, fraction = '', tz = 'Z'] = match;
  const epochMs = Date.parse(`${datePart}${tz}`);
  if (Number.isNaN(epochMs)) return null;
  const micros = Number((fraction + '000000').slice(0, 6));
  return epochMs * 1000 + micros;
};

/**
 * Computes a compact, human-readable duration between two ISO timestamps with
 * microsecond precision. Only non-zero units are included, ordered from largest
 * to smallest (months → weeks → days → hours → minutes → seconds → milliseconds).
 * Negative durations are prefixed with `-`.
 *
 * @param {string | null | undefined} start - Start timestamp.
 * @param {string | null | undefined} end - End timestamp.
 * @param {string} [fallback='—'] - Returned when either timestamp is missing or invalid.
 * @returns {string} Formatted duration (e.g., "2h 14m 3s"), `"0 ms"` when start and end
 *  are equal, or `fallback` when input is missing/invalid.
 */
export const formatDurationBetween = (
  start: string | null | undefined,
  end: string | null | undefined,
  fallback = '—'
): string => {
  const startMicros = toEpochMicros(start);
  const endMicros = toEpochMicros(end);
  if (startMicros === null || endMicros === null) return fallback;
  const totalMicros = endMicros - startMicros;
  if (totalMicros === 0) return '0 ms';

  const sign = totalMicros < 0 ? '-' : '';
  const absMicros = Math.abs(totalMicros);
  const wholeMs = Math.floor(absMicros / 1000);
  const subMs = absMicros - wholeMs * 1000;

  const d = dayjs.duration(wholeMs);
  const months = Math.floor(d.asMonths());
  const weeks = Math.floor(d.subtract(months, 'months').asWeeks());
  const rest = d.subtract(months, 'months').subtract(weeks, 'weeks');
  const millis = rest.milliseconds();
  const msExact = subMs === 0 ? `${millis}` : (millis + subMs / 1000).toFixed(3).replace(/\.?0+$/, '');

  const parts = [
    [months, 'mo'],
    [weeks, 'w'],
    [rest.days(), 'd'],
    [rest.hours(), 'h'],
    [rest.minutes(), 'm'],
    [rest.seconds(), 's'],
    [msExact !== '0' ? msExact : 0, 'ms'],
  ]
    .filter(([v]) => v)
    .map(([v, u]) => `${v}${u}`);

  return sign + (parts.length ? parts.join(' ') : '0 ms');
};
